import { type Static, Type } from "typebox";

import {
  InvitationStatus,
  MembershipRole,
  OrganizationIndustry,
} from "./enums.js";
import { defineRoute } from "./route.js";
import {
  ErrorEnvelope,
  errors,
  Exact,
  Identifier,
  NoContent,
  Nullable,
  StringEnum,
  Timestamp,
} from "./schema.js";

// --- Health ----------------------------------------------------------------

export const Health = Exact({
  ok: Type.Boolean(),
  /**
   * The commit this build came from. Optional because a local run has no
   * revision to report, and present in a deployment so that "is the thing
   * I just merged actually live" has an answer that is not a guess.
   */
  revision: Type.Optional(Type.String()),
  service: Type.Literal("api"),
});
export type Health = Static<typeof Health>;

export const ReadinessError = Exact({
  ...ErrorEnvelope.properties,
  ok: Type.Literal(false),
  service: Type.Literal("api"),
});
export type ReadinessError = Static<typeof ReadinessError>;

export const getHealth = defineRoute({
  method: "GET",
  path: "/health",
  response: { 200: Health },
  tags: ["health"],
});

export const getLiveness = defineRoute({
  method: "GET",
  path: "/health/live",
  response: { 200: Health },
  tags: ["health"],
});

export const getReadiness = defineRoute({
  method: "GET",
  path: "/health/ready",
  response: { 200: Health, 503: ReadinessError },
  tags: ["health"],
});

// --- The signed-in person ----------------------------------------------------

export const OrganizationMembership = Exact({
  membershipId: Identifier,
  name: Type.String(),
  organizationId: Identifier,
  role: MembershipRole,
});
export type OrganizationMembership = Static<typeof OrganizationMembership>;

export const MyOrganizations = Exact({
  organizations: Type.Array(OrganizationMembership),
});
export type MyOrganizations = Static<typeof MyOrganizations>;

export const listMyOrganizations = defineRoute({
  method: "GET",
  path: "/me/organizations",
  response: { 200: MyOrganizations, ...errors(401, 403) },
  tags: ["account"],
});

/**
 * The read-only sample fleet (REL-001), for looking round before setting
 * up a company. 404 when this deployment has none.
 */
export const SampleOrganization = Exact({
  name: Type.String(),
  organizationId: Identifier,
});
export type SampleOrganization = Static<typeof SampleOrganization>;

export const getSampleOrganization = defineRoute({
  method: "GET",
  path: "/sample-organization",
  response: { 200: SampleOrganization, ...errors(401, 404) },
  tags: ["account"],
});

export const Profile = Exact({
  connectedProviders: Type.Array(Type.String()),
  displayName: Nullable(Type.String()),
  email: Type.String(),
  emailVerified: Type.Boolean(),
  hasPassword: Type.Boolean(),
  timezone: Nullable(Type.String()),
});
export type Profile = Static<typeof Profile>;

export const getProfile = defineRoute({
  method: "GET",
  path: "/me/profile",
  response: { 200: Profile, ...errors(401, 404) },
  tags: ["account"],
});

export const updateProfile = defineRoute({
  body: Exact({
    displayName: Type.Optional(Type.String({ maxLength: 120, minLength: 1 })),
    timezone: Type.Optional(Nullable(Type.String({ maxLength: 64 }))),
  }),
  method: "PATCH",
  path: "/me/profile",
  response: { 200: Profile, ...errors(401, 422) },
  tags: ["account"],
});

// --- Organizations and their members ---------------------------------------

export const Organization = Exact({
  /** False when the request found an organization it had already created. */
  created: Type.Boolean(),
  /** ISO 4217. How money displays for everyone in the company. */
  currency: Type.String(),
  distanceUnit: StringEnum(["miles"]),
  id: Identifier,
  industry: Nullable(OrganizationIndustry),
  name: Type.String(),
  onboardingCompletedAt: Nullable(Type.String()),
  timezone: Nullable(Type.String()),
});
export type Organization = Static<typeof Organization>;

export const createOrganization = defineRoute({
  body: Exact({
    /**
     * ISO 4217. Optional for clients written before it was asked; the setup
     * screen always sends it, confirmed. Defaults to USD.
     */
    currency: Type.Optional(Type.String({ pattern: "^[A-Z]{3}$" })),
    // Miles only, for now. The MVP defines mileage as an odometer reading in
    // miles throughout, and offering a choice the rest of the product ignores
    // would be worse than not offering one.
    distanceUnit: Type.Optional(StringEnum(["miles"])),
    expectedVehicleCount: Type.Optional(
      Type.Integer({ maximum: 10_000, minimum: 0 }),
    ),
    industry: Type.Optional(Nullable(OrganizationIndustry)),
    name: Type.String({ maxLength: 200, minLength: 1 }),
    /**
     * One per setup attempt, generated by the client and reused on every
     * retry of it. A retry with the same key returns the company the first
     * attempt made, even when a field was edited in between.
     */
    setupKey: Type.Optional(Type.String({ maxLength: 100, minLength: 8 })),
    timezone: Type.String({ maxLength: 64, minLength: 1 }),
  }),
  method: "POST",
  path: "/organizations",
  // 201 when created, 200 when a retry found the one it already made.
  response: { 200: Organization, 201: Organization, ...errors(401, 422) },
  tags: ["organizations"],
});

export const completeOnboarding = defineRoute({
  method: "POST",
  path: "/organizations/:organizationId/onboarding/complete",
  response: { 200: Organization, ...errors(401, 403) },
  tags: ["organizations"],
});

export const Member = Exact({
  email: Type.String(),
  id: Identifier,
  name: Type.String(),
  role: MembershipRole,
});
export type Member = Static<typeof Member>;

export const PendingInvitation = Exact({
  email: Type.String(),
  expiresAt: Timestamp,
  id: Identifier,
  role: MembershipRole,
});
export type PendingInvitation = Static<typeof PendingInvitation>;

export const MemberManagement = Exact({
  invitations: Type.Array(PendingInvitation),
  members: Type.Array(Member),
});
export type MemberManagement = Static<typeof MemberManagement>;

export const listMembers = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/members",
  response: { 200: MemberManagement, ...errors(401, 403) },
  tags: ["organizations"],
});

export const Membership = Exact({ id: Identifier, role: MembershipRole });
export type Membership = Static<typeof Membership>;

export const changeMemberRole = defineRoute({
  body: Exact({ role: MembershipRole }),
  method: "PATCH",
  path: "/organizations/:organizationId/members/:membershipId",
  response: { 200: Membership, ...errors(401, 403, 409) },
  tags: ["organizations"],
});

export const removeMember = defineRoute({
  method: "DELETE",
  path: "/organizations/:organizationId/members/:membershipId",
  response: { 204: NoContent, ...errors(401, 403, 409) },
  tags: ["organizations"],
});

export const revokeInvitation = defineRoute({
  method: "DELETE",
  path: "/organizations/:organizationId/invitations/:invitationId",
  response: { 204: NoContent, ...errors(401, 403) },
  tags: ["organizations"],
});

export const Invitation = Exact({
  email: Type.String(),
  expiresAt: Timestamp,
  id: Identifier,
  organizationId: Identifier,
  /** True when an invitation to this address was already pending. */
  resent: Type.Boolean(),
  role: MembershipRole,
});
export type Invitation = Static<typeof Invitation>;

export const createInvitation = defineRoute({
  body: Exact({
    /**
     * Set after the sender has confirmed that sending again changes the role
     * of the invitation already pending for this address. Without it, such a
     * change answers 409 invitation_role_change_unconfirmed.
     */
    confirmRoleChange: Type.Optional(Type.Boolean()),
    email: Type.String({ minLength: 3 }),
    linkedDriverId: Type.Optional(Identifier),
    role: MembershipRole,
  }),
  method: "POST",
  path: "/organizations/:organizationId/invitations",
  response: { 201: Invitation, ...errors(400, 403, 409) },
  tags: ["organizations"],
});

const InvitationToken = Exact({ token: Type.String({ minLength: 1 }) });

/**
 * What someone holding a live invitation is shown before accepting. The
 * address is masked, since a link can be forwarded.
 */
export const InvitationPreview = Exact({
  email: Type.String(),
  inviterName: Nullable(Type.String()),
  organizationName: Type.String(),
  role: MembershipRole,
});
export type InvitationPreview = Static<typeof InvitationPreview>;

export const InvitationResolution = Exact({
  /** Only for a pending invitation; any other status reveals nothing more. */
  invitation: Type.Optional(InvitationPreview),
  status: InvitationStatus,
});
export type InvitationResolution = Static<typeof InvitationResolution>;

export const resolveInvitation = defineRoute({
  body: InvitationToken,
  method: "POST",
  path: "/invitations/resolve",
  response: { 200: InvitationResolution, ...errors(429) },
  tags: ["invitations"],
});

/**
 * The holder of an expired or revoked link asks the company for a new one.
 * Always 204, whatever the link was, so it reveals nothing.
 */
export const requestNewInvitation = defineRoute({
  body: InvitationToken,
  method: "POST",
  path: "/invitations/renewal-requests",
  response: { 204: NoContent, ...errors(429) },
  tags: ["invitations"],
});

export const AcceptedInvitation = Exact({
  membershipId: Type.String(),
  organizationId: Type.String(),
});
export type AcceptedInvitation = Static<typeof AcceptedInvitation>;

export const acceptInvitation = defineRoute({
  body: InvitationToken,
  method: "POST",
  path: "/invitations/accept",
  response: { 200: AcceptedInvitation, ...errors(400, 401, 403, 409) },
  tags: ["invitations"],
});
